import { inject, Injectable, Renderer2, RendererFactory2, Signal, signal, WritableSignal } from '@angular/core';
import { MenuAction } from '@shared/interfaces/header/menu-action';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class ContextMenuService {
  private readonly translateService: TranslateService = inject(TranslateService);
  private menuElement?: HTMLElement;

  private readonly isOpenSignal: WritableSignal<boolean> = signal(false);

  /**
   * Read-only signal indicating whether the context menu is currently open.
   * Subscribe to this to react to open/close transitions.
   */
  public readonly openState: Signal<boolean> = this.isOpenSignal.asReadonly();

  private readonly renderer?: Renderer2;

  constructor(
    private readonly renderFactory: RendererFactory2,
  ) {
    this.renderer = this.renderFactory.createRenderer(null, null);
  }

  public openMenu(x: number, y: number, actions: MenuAction[]): void {
    if (this.isOpenSignal()) {
      this.closeMenu();
    }

    this.isOpenSignal.set(true);

    this.createMenu(x, y, actions);
    this.appendToBody();
  }

  public open(): boolean {
    return this.isOpenSignal();
  }

  private createMenu(x: number, y: number, actions: MenuAction[]): void {
    if (!this.renderer) return;

    /** Create Element **/
    this.menuElement = this.renderer.createElement('div');

    /** Add classes for styling **/
    this.renderer.addClass(this.menuElement, 'context-menu');
    this.renderer.addClass(this.menuElement, 'fixed');
    this.renderer.addClass(this.menuElement, 'rounded-md');
    this.renderer.addClass(this.menuElement, 'text-sm');
    this.renderer.addClass(this.menuElement, 'text-white');
    this.renderer.addClass(this.menuElement, 'bg-black');
    this.renderer.addClass(this.menuElement, 'p-2');
    this.renderer.addClass(this.menuElement, 'z-[9999]');
    this.renderer.addClass(this.menuElement, 'shadow');

    /** Transitions **/
    this.renderer.addClass(this.menuElement, 'transition-all');
    this.renderer.addClass(this.menuElement, 'duration-200');
    this.renderer.addClass(this.menuElement, 'ease-out');

    /** Positioning **/
    this.renderer.setStyle(this.menuElement, 'top', `${y}px`);
    this.renderer.setStyle(this.menuElement, 'left', `${x}px`);

    /** Build actions **/
    for (let index = 0; index < actions.length; index++) {
      const action: MenuAction = actions[index];

      // Breaker renders as separator
      if (action.breaker && index !== 0) {
        const sep = this.renderer!.createElement('div');
        this.renderer!.addClass(sep, 'my-2');
        this.renderer!.addClass(sep, 'h-[1px]');
        this.renderer!.addClass(sep, 'bg-white/20');
        this.renderer!.appendChild(this.menuElement, sep);
      }

      const item = this.renderer!.createElement('button');
      this.renderer!.addClass(item, 'flex');
      this.renderer!.addClass(item, 'items-center');
      this.renderer!.addClass(item, 'gap-2');
      this.renderer!.addClass(item, 'px-3');
      this.renderer!.addClass(item, 'py-1.5');
      this.renderer!.addClass(item, 'rounded');
      this.renderer!.addClass(item, 'w-full');
      this.renderer!.addClass(item, 'text-left');

      /** Hover Disabled Styling **/
      this.renderer!.addClass(item, 'hover:bg-white/20');
      const disabled = typeof action.disabled === 'function' ? action.disabled() : !!action.disabled;
      if (disabled) {
        this.renderer!.setAttribute(item, 'disabled', 'true');
        this.renderer!.addClass(item, 'opacity-50');
        this.renderer!.addClass(item, 'cursor-not-allowed');
      } else {
        // attach click handler
        this.renderer!.listen(item, 'click', (e: MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          try { action.callback?.(); } finally { this.closeMenu(); }
        });
      }

      const label = this.renderer!.createElement('span');
      this.renderer!.setProperty(label, 'textContent', this.translateService.instant(action.title));
      this.renderer!.appendChild(item, label);

      this.renderer!.appendChild(this.menuElement, item);
    }
  }

  private appendToBody(): void {
    if (!this.renderer || !this.menuElement) return;
    /** Add Element to Body **/
    this.renderer.appendChild(document.body, this.menuElement);
  }

  public closeMenu(): void {
    this.isOpenSignal.set(false);
    if (!this.renderer || !this.menuElement) return;
    this.renderer.removeChild(document.body, this.menuElement);
    this.menuElement = undefined;
  }
}
